import {
  HomeProductReflectionsQueryResult,
  useHomeProductReflectionsLazyQuery,
} from '@graphql/queries/__generated__/home.generated'
import Skeleton from '@mui/material/Skeleton'
import makeStyles from '@mui/styles/makeStyles'
import PlaceHolder from 'images/placeholder-img.svg'
import _ from 'lodash'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import { Link } from 'react-router-dom'
import { SwiperOptions } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import ProductDetailsDialog from '../../../components/Product/ProductDetailsDialog'
import { getProductUrl } from '../../../urls'
import { maybe } from '../../../utils/misc'
import { useTranslation } from 'react-i18next'
import { useIsVisible } from '../../../hooks/useIsVisible'
import FormattedPriceRange from '@components/FormattedPriceRange'
import { Ordering } from '../../../../generated/gqlTypes'

const MAX_SHOW_PRODUCTS = 18
const useStyles = makeStyles(theme => ({
  root: {
    '&:hover $detailsWrapper': {
      opacity: 1,
    },
  },
  detailsWrapper: {
    opacity: 0,
    transition: 'opacity .25s ease',
    backgroundColor: 'hsl(0deg 0% 100% / 80%)',
  },
  placeHolder: {
    paddingBottom: '137%',
  },
}))

const PopularProductsSkeleton: React.FC<{ swiperOptions: SwiperOptions }> = ({ swiperOptions }) => {
  const classes = useStyles()
  return (
    <Swiper {...swiperOptions}>
      {_.range(6).map((number, idx) => {
        return (
          <SwiperSlide key={idx}>
            <div className={`relative block no-underline ${classes.root}`}>
              <div className='absolute inset-0 h-full'>
                <LazyLoadImage
                  alt={`Popular Product Skeleton ${number}`}
                  effect='opacity'
                  src={PlaceHolder}
                  wrapperClassName='w-full'
                  className='w-full h-full object-cover object-top'
                />
              </div>
              <div className={classes.placeHolder} />
              <div className='md:hidden pt-12'>
                <div className='text-13 line-clamp-3'>
                  <Skeleton width={200} />
                </div>
                <div className='text-13 font-bold'>
                  <Skeleton width={50} />
                </div>
              </div>
            </div>
          </SwiperSlide>
        )
      })}
    </Swiper>
  )
}

interface NewestProductsProps {}

const PopularProducts: React.FC<NewestProductsProps> = ({}) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const ref = useRef()
  const isVisible = useIsVisible(ref, { threshold: 0.25 })
  const [activeProductData, setActiveProductData] = useState(null)

  const [loadReflections, reflectionsQueryResult] = useHomeProductReflectionsLazyQuery({
    variables: {
      first: 30,
      orderBy: [{ orderCount: Ordering.Desc }],
    },
  })
  useEffect(() => {
    if (isVisible && !reflectionsQueryResult.data) {
      loadReflections()
    }
  }, [isVisible, reflectionsQueryResult.data])
  const reflections: HomeProductReflectionsQueryResult['data']['specifics']['edges'][0]['node'][] = useMemo(
    () =>
      maybe(
        () =>
          _.sampleSize(
            reflectionsQueryResult.data.specifics.edges.map(edge => edge.node),
            MAX_SHOW_PRODUCTS
          ),
        []
      ),
    [reflectionsQueryResult.data]
  )

  const swiperOptions: SwiperOptions = {
    spaceBetween: 10,
    slidesPerView: 6,
    lazy: true,
    preloadImages: false,
    pagination: {
      clickable: true,
      el: `#swiper-pagination-popular`,
    },
    watchSlidesProgress: true,
    loop: false,
    allowSlideNext: reflections.length > 1,
    allowSlidePrev: reflections.length > 1,
    navigation: {
      prevEl: `#swiper-button-prev-popular`,
      nextEl: `#swiper-button-next-popular`,
    },
    autoplay: {
      disableOnInteraction: true,
      delay: 5000,
      waitForTransition: true,
    },

    breakpoints: {
      0: {
        slidesPerView: 2,
        slidesPerGroup: 2,
        speed: 300,
      },
      600: {
        slidesPerView: 3,
        slidesPerGroup: 3,
        speed: 300,
      },
      800: {
        slidesPerView: 4,
        slidesPerGroup: 4,
        speed: 1000,
      },
      1000: {
        slidesPerView: 5,
        slidesPerGroup: 5,
        speed: 1000,
      },
      1200: {
        slidesPerView: 6,
        slidesPerGroup: 6,
        speed: 1000,
      },
    },
  }
  const handleClickLink = spec => e => {
    e.preventDefault()
    setActiveProductData({ pk: spec.pk, slug: spec.slug })
  }

  return (
    <section className='my-40' ref={ref}>
      <div className='text-center my-30'>
        <h3 className='font-bold text-5xl mb-20 font-futura'>
          {t('title.mostPopularProducts', 'Most Popular Products')}
        </h3>
        <div className='py-20 px-20 md:px-40 bg-orange text-lg md:text-2xl leading-normal'>
          {t(
            'description.mostPopularProducts',
            'Make incredible handmade with our huge selection of cross-stitch, embroidery and diamond ' +
              'painting models! Here you can find over 1000 unique designs that are perfect for your future projects. ' +
              'Browse all our patterns and kits to find the design of your dreams!'
          )}
        </div>
      </div>
      <div className='relative px-10 md:px-20 lg:px-30'>
        {!reflections.length && reflectionsQueryResult.loading && (
          <PopularProductsSkeleton swiperOptions={swiperOptions} />
        )}
        <Swiper {...swiperOptions}>
          {reflections.map((node, idx) => {
            const minPrice = node.product.priceRange.discount.min
            const maxPrice = node.product.priceRange.discount.max
            const currency = node.product.priceRange.currency
            return (
              <SwiperSlide key={node.id}>
                <Link
                  className={`relative block no-underline ${classes.root}`}
                  to={getProductUrl(node.slug, node.pk)}
                  onClick={handleClickLink(node)}
                >
                  <div className='absolute inset-0 h-full'>
                    <LazyLoadImage
                      alt={node.name}
                      effect='opacity'
                      placeholderSrc={PlaceHolder}
                      src={node.product.thumbnail.lg}
                      className='w-full h-full object-contain object-top'
                    />
                  </div>
                  <div className={classes.placeHolder} />
                  <div className={`hidden md:block absolute bottom-0 left-0 right-0 p-12 ${classes.detailsWrapper}`}>
                    <div className='text-13 line-clamp-3'>{node.shortName || node.name}</div>
                    <div className='text-13 font-bold'>
                      <FormattedPriceRange currency={currency} minAmount={minPrice} maxAmount={maxPrice} />
                    </div>
                  </div>
                  <div className='md:hidden pt-12'>
                    <div className='text-13 line-clamp-3'>{node.shortName || node.name}</div>
                    <div className='text-13 font-bold'>
                      <FormattedPriceRange currency={currency} minAmount={minPrice} maxAmount={maxPrice} />
                    </div>
                  </div>
                </Link>
              </SwiperSlide>
            )
          })}
        </Swiper>
        <div
          id={`swiper-pagination-popular`}
          className={`swiper-pagination swiper-pagination-clickable swiper-pagination-bullets w-full ${
            reflections.length > 1 ? 'block' : 'hidden'
          } md:hidden`}
        />
        <div id={`swiper-button-prev-popular`} className='swiper-button-prev hidden md:block' />
        <div id={`swiper-button-next-popular`} className='swiper-button-next hidden md:block' />
      </div>
      <ProductDetailsDialog
        isOpen={!!activeProductData}
        setIsOpen={setActiveProductData}
        spec={reflections.find(spec => spec.pk === activeProductData?.pk)}
      />
    </section>
  )
}

export default PopularProducts
